import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  Card,
  CardHeader,
  CardContent,
  Switch,
  Grid,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Box,
} from "@mui/material";
import { CalendarChangeParams } from "primereact/calendar";
import { DropdownChangeParams } from "primereact/dropdown";
import { InputNumber, InputNumberChangeParams } from "primereact/inputnumber";

import { InternationalDatepicker } from "../../shared/InternationalDatepicker";
import { getStartOfDay, getEndOfDay, getDateObject } from "../../shared/DateConversions";
import { BulkWorkorderContext } from "./BulkWorkorderContext";
import LabelText from "../../shared/LabelValueFormatters/LabelText";
import ValueText from "../../shared/LabelValueFormatters/ValueText";
import { RepeatBy } from "./useRecurringTargetDates";
import { RepeatIntervalDropdown } from "./RepeatIntervalDropdown";

export function WorkorderTargetDates() {
  const { t } = useTranslation();
  const {
    workorderDetails,
    recurrenceEnabled,
    setRecurrenceEnabled,
    recurrenceSettings,
    setRepeatBy,
    setRepeatInterval,
    setRepeatEndDate,
    setRepeatCount,
    updateTargetStartDate,
    updateTargetCompletionDate,
    targetDates,
  } = useContext(BulkWorkorderContext);

  function handleRecurrenceEnabledSwitchChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
    setRecurrenceEnabled(checked);
  }

  function handleRecurrenceTypeChange(event: React.ChangeEvent<HTMLInputElement>, selectedValue: string) {
    setRepeatBy(selectedValue as RepeatBy);
  }

  function handleRepeatIntervalChange(e: DropdownChangeParams) {
    setRepeatInterval(e.value);
  }

  function handleRepeatEndDateChange(e: CalendarChangeParams | undefined) {
    setRepeatEndDate(e?.value as Date);
  }

  function handleRepeatCountChange(e: InputNumberChangeParams) {
    setRepeatCount(e.value as number);
  }

  function handleTargetStartDateChange(e: CalendarChangeParams | undefined) {
    const targetStartDate = e?.value ? getStartOfDay(e?.value as Date) : undefined;

    updateTargetStartDate(targetStartDate);
  }

  function handleTargetCompletionDateChange(e: CalendarChangeParams | undefined) {
    const targetCompletionDate = e?.value ? getEndOfDay(e?.value as Date) : undefined;

    updateTargetCompletionDate(targetCompletionDate);
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} lg={6}>
        <Card>
          <CardHeader
            title={t("workorders.targetDates")}
            titleTypographyProps={{ variant: "h6", color: "textSecondary" }}
          ></CardHeader>
          <Divider />
          <CardContent sx={{ pt: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2} my={2}>
              <LabelText>{t("workorders.targetStartDate")}:</LabelText>

              <InternationalDatepicker
                value={getDateObject(workorderDetails.targetStartDate)}
                maxDate={getDateObject(workorderDetails.targetCompletionDate)}
                onChange={handleTargetStartDateChange}
                showIcon
              />
            </Stack>

            <Divider />
            <Stack direction="row" alignItems="center" spacing={2} my={2}>
              <LabelText>{t("workorders.targetCompletionDate")}:</LabelText>

              <InternationalDatepicker
                value={getDateObject(workorderDetails.targetCompletionDate)}
                minDate={getDateObject(workorderDetails.targetStartDate)}
                onChange={handleTargetCompletionDateChange}
                showIcon
              />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Card>
          <CardHeader
            title={
              <>
                {t("workorders.recurrence")}
                <Switch checked={recurrenceEnabled} onChange={handleRecurrenceEnabledSwitchChange} />
              </>
            }
            titleTypographyProps={{ variant: "h6", color: "textSecondary" }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2} divider={<Divider />}>
              <Box display="flex" justifyContent="flex-start">
                <FormControlLabel
                  disabled={!recurrenceEnabled}
                  control={<RepeatIntervalDropdown onDropdownChange={handleRepeatIntervalChange} />}
                  labelPlacement="start"
                  sx={{ m: 0, display: "flex", alignItems: "center" }}
                  label={<LabelText mr={1}>{t("workorders.repeat")}:</LabelText>}
                />
              </Box>

              <RadioGroup value={recurrenceSettings?.repeatBy || "endDate"} onChange={handleRecurrenceTypeChange}>
                <FormControlLabel
                  disabled={!recurrenceEnabled}
                  value={"endDate" as RepeatBy}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center">
                      <ValueText mr={1}>{t("workorders.until")}:</ValueText>

                      <InternationalDatepicker
                        value={
                          recurrenceSettings?.repeatBy === "endDate" ? recurrenceSettings?.repeatEndDate : undefined
                        }
                        minDate={getDateObject(workorderDetails.targetCompletionDate)}
                        onChange={handleRepeatEndDateChange}
                        showIcon
                        disabled={!recurrenceEnabled || recurrenceSettings?.repeatBy !== "endDate"}
                      />
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={"count" as RepeatBy}
                  control={<Radio />}
                  disabled={!recurrenceEnabled}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ValueText>{t("workorders.for")}</ValueText>
                      <InputNumber
                        value={recurrenceSettings?.repeatBy === "count" ? recurrenceSettings?.repeatCount : undefined}
                        showButtons
                        min={0}
                        size={3}
                        disabled={!recurrenceEnabled || recurrenceSettings?.repeatBy !== "count"}
                        onChange={handleRepeatCountChange}
                      />
                      <ValueText>{t("workorders.occurrences")}</ValueText>
                    </Stack>
                  }
                />
              </RadioGroup>

              {recurrenceEnabled && (
                <Box>
                  <Typography variant="body2">{t("workorders.recurrenceListLabel")}</Typography>
                  <Table sx={{ mt: 0 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("workorders.targetStartDate")}</TableCell>
                        <TableCell>{t("workorders.targetCompletionDate")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {targetDates.map((date) => (
                        <TableRow>
                          <TableCell>{date.targetStartDate?.toLocaleDateString()}</TableCell>
                          <TableCell>{date.targetCompletionDate?.toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
